import React from "react";
import {
  Box,
  Heading,
  Input,
  HStack,
  Button,
  Divider,
  Text,
  Tooltip,
} from "@chakra-ui/react";

const SectionBox = ({ title, children }) => (
  <Box
    mt={6}
    p={4}
    border="2px solid"
    borderColor="gray.600"
    borderRadius="md"
    bg="white"
    boxShadow="md"
    textAlign="left"
  >
    <Heading size="md" mb={2}>{title}</Heading>
    <Divider mb={4} />
    {children}
  </Box>
);

const PhysicianSection = ({ physicianInfo, setPhysicianInfo, facilityAddress, originalData = {} }) => {
  const getNestedValue = (obj, path) => path.split(".").reduce((acc, part) => acc?.[part], obj);

  const getBorderColor = (name, value) => {
    const original = getNestedValue(originalData, name);
    const hasOriginal = original !== undefined && original !== null && original !== "";
    const hasValue = value !== undefined && value !== null && value !== "";

    if (!hasOriginal && hasValue) return "2px solid green"; // Added
    if (hasOriginal && !hasValue) return "2px solid red";   // Deleted
    if (hasOriginal && hasValue && original !== value) return "2px solid blue"; // Changed
    return "1px solid #E2E8F0"; // Unchanged
  };

  const getTooltipLabel = (name, value) => {
    const original = getNestedValue(originalData, name);
    const hasOriginal = original !== undefined && original !== null && original !== "";
    const hasValue = value !== undefined && value !== null && value !== "";

    if (!hasOriginal && hasValue) return "Added new value";
    if (hasOriginal && !hasValue) return `Deleted previous value: ${original}`;
    if (hasOriginal && hasValue && original !== value) return `Changed from: ${original}`;
    return "";
  };

  const renderInput = (label, name, value, onChange) => (
    <Box flex="1">
      <Text fontSize="sm" mb={1}>{label}</Text>
      <Tooltip label={getTooltipLabel(name, value)} isDisabled={!getTooltipLabel(name, value)}>
        <Input
          value={value || ""}
          onChange={onChange}
          border={getBorderColor(name, value)}
        />
      </Tooltip>
    </Box>
  );

  return (
    <SectionBox title="Physician Information">
      <HStack spacing={4} mb={3}>
        {renderInput("Physician First Name", "physicianInfo.physicianFirstName", physicianInfo.physicianFirstName, (e) =>
          setPhysicianInfo({ ...physicianInfo, physicianFirstName: e.target.value })
        )}
        {renderInput("Physician Last Name", "physicianInfo.physicianLastName", physicianInfo.physicianLastName, (e) =>
          setPhysicianInfo({ ...physicianInfo, physicianLastName: e.target.value })
        )}
      </HStack>

      <HStack spacing={4} mb={3}>
        {renderInput("Phone", "physicianInfo.phone", physicianInfo.phone, (e) =>
          setPhysicianInfo({ ...physicianInfo, phone: e.target.value })
        )}
        {renderInput("NPI", "physicianInfo.npi", physicianInfo.npi, (e) =>
          setPhysicianInfo({ ...physicianInfo, npi: e.target.value })
        )}
      </HStack>

      <HStack spacing={4} mb={3}>
        {renderInput("Contact Name", "physicianInfo.contactName", physicianInfo.contactName, (e) =>
          setPhysicianInfo({ ...physicianInfo, contactName: e.target.value })
        )}
        {renderInput("Contact Phone", "physicianInfo.contactPhone", physicianInfo.contactPhone, (e) =>
          setPhysicianInfo({ ...physicianInfo, contactPhone: e.target.value })
        )}
      </HStack>

      <Box mb={3}>
        {renderInput("Fax", "physicianInfo.fax", physicianInfo.fax, (e) =>
          setPhysicianInfo({ ...physicianInfo, fax: e.target.value })
        )}
      </Box>

      <HStack spacing={4} mb={3}>
        {renderInput("TIN", "physicianInfo.tin", physicianInfo.tin, (e) =>
          setPhysicianInfo({ ...physicianInfo, tin: e.target.value })
        )}
        {renderInput("PTAN", "physicianInfo.ptan", physicianInfo.ptan, (e) =>
          setPhysicianInfo({ ...physicianInfo, ptan: e.target.value })
        )}
      </HStack>

      <Box mb={3}>
        {renderInput("Specialty", "physicianInfo.specialty", physicianInfo.specialty, (e) =>
          setPhysicianInfo({ ...physicianInfo, specialty: e.target.value })
        )}
      </Box>

      <HStack spacing={4} mb={3}>
        {renderInput("Street Address", "physicianInfo.street", physicianInfo.street, (e) =>
          setPhysicianInfo({ ...physicianInfo, street: e.target.value })
        )}
        {renderInput("City", "physicianInfo.city", physicianInfo.city, (e) =>
          setPhysicianInfo({ ...physicianInfo, city: e.target.value })
        )}
      </HStack>

      <HStack spacing={4} mb={3}>
        {renderInput("State", "physicianInfo.state", physicianInfo.state, (e) =>
          setPhysicianInfo({ ...physicianInfo, state: e.target.value })
        )}
        {renderInput("Zip Code", "physicianInfo.zip", physicianInfo.zip, (e) =>
          setPhysicianInfo({ ...physicianInfo, zip: e.target.value })
        )}
      </HStack>

      <Button
        size="sm"
        onClick={() =>
          setPhysicianInfo({
            ...physicianInfo,
            street: facilityAddress.street,
            city: facilityAddress.city,
            state: facilityAddress.state,
            zip: facilityAddress.zip,
          })
        }
      >
        Same as Facility
      </Button>
    </SectionBox>
  );
};

export default PhysicianSection;
